import { wrapReducer } from "@client/content/lib/helper";
import { type FailureActionData } from "@client/lib/actions";
import { Status } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import {
  Actions,
  type GetLessonDetailBeginActionData,
  type GetLessonDetailSuccessActionData,
  type InitSuccessActionData,
  type LessonEventSuccessActionData,
  type LoadActiveCourseSuccessActionData,
  type QuizGoToActionData,
  type QuizProceedBeginActionData,
  type QuizProceedSuccessActionData,
  type QuizReviewBeginActionData,
  type QuizReviewEndActionData,
  type QuizReviewSuccessActionData,
  type SetActiveCourseIdActionData,
  type SetActiveLessonIdActionData,
  type SetActiveSectionIdActionData
} from "./actions";
import { type CourseDetail } from "./types";

export const defaultDetailState: CourseDetail.AppStateDetail = {
  activeCourse: null,
  activeCourseId: null,
  lessonDetail: {},
  id: ""
};

export const reducerDetail = <T extends CourseDetail.AppStateDetail>(
  state: T = defaultDetailState as any,
  action: ReduxAction<any>
): T => {
  switch (action.type) {
    case Actions.SetActiveCourseId: {
      const { courseId } = action.data as SetActiveCourseIdActionData;
      return {
        ...state,
        activeCourseId: courseId
      };
    }

    case Actions.SetActiveLessonId: {
      const { lessonId } = action.data as SetActiveLessonIdActionData;
      return {
        ...state,
        activeCourse: {
          ...state.activeCourse!,
          activeLessonId: lessonId
        }
      };
    }

    case Actions.SetActiveSectionId: {
      const { sectionId } = action.data as SetActiveSectionIdActionData;
      return {
        ...state,
        activeCourse: {
          ...state.activeCourse!,
          activeModuleId: sectionId
        }
      };
    }

    case Actions.LessonEventSuccess: {
      const { lessons } = action.data as LessonEventSuccessActionData;

      const lessonIdToLesson = lessons.reduce((res, { lessonId, ...x }) => ({ ...res, [lessonId]: x }), {});
      return {
        ...state,
        activeCourse: {
          ...state.activeCourse!,
          lessons: state.activeCourse!.lessons.map<CourseDetail.CourseModuleLesson>((x) => ({
            ...x,
            ...(x.id in lessonIdToLesson ? lessonIdToLesson[x.id] : {})
          }))
        }
      };
    }

    case Actions.LoadActiveCourseBegin: {
      return {
        ...state,
        activeCourse: null
      };
    }

    case Actions.LoadActiveCourseFailure: {
      return {
        ...state,
        activeCourseId: null,
        activeCourse: null
      };
    }

    case Actions.LoadActiveCourseSuccess: {
      const activeCourse = action.data as LoadActiveCourseSuccessActionData;
      return {
        ...state,
        activeCourse
      };
    }

    case Actions.GetLessonDetailSuccess: {
      const { lessonId } = action.params as GetLessonDetailBeginActionData;
      const detail = action.data as GetLessonDetailSuccessActionData;
      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...detail,
            error: null,
            status: "ready"
          }
        }
      };
    }

    case Actions.QuizGoTo: {
      const { lessonId, questionId } = action.data as QuizGoToActionData;
      const quizState = state.lessonDetail[lessonId].quizState!;

      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            quizState: {
              ...quizState,
              question: quizState.seenQuestions.find((x) => x.id === questionId)
            }
          }
        }
      };
    }

    case Actions.QuizProceedBegin: {
      const { lessonId } = action.params as QuizProceedBeginActionData;
      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            error: null,
            status: "busy"
          }
        }
      };
    }

    case Actions.QuizProceedFailure: {
      const { lessonId } = action.params as QuizProceedBeginActionData;
      const { error } = action.data as FailureActionData;

      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            error,
            status: "ready"
          }
        }
      };
    }

    case Actions.QuizProceedSuccess: {
      const { lessonId } = action.params as QuizProceedBeginActionData;
      const data = action.data as QuizProceedSuccessActionData;
      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            quizState: data,
            status: "ready"
          }
        }
      };
    }

    case Actions.QuizReviewBegin: {
      const { lessonId } = action.params as QuizReviewBeginActionData;
      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            error: null,
            status: "busy"
          }
        }
      };
    }

    case Actions.QuizReviewFailure: {
      const { lessonId } = action.params as QuizReviewBeginActionData;
      const { error } = action.data as FailureActionData;

      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            error,
            status: "ready"
          }
        }
      };
    }

    case Actions.QuizReviewSuccess: {
      const { lessonId } = action.params as QuizReviewBeginActionData;
      const data = action.data as QuizReviewSuccessActionData;
      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            quizReview: data,
            status: "ready"
          }
        }
      };
    }

    case Actions.QuizReviewEnd: {
      const { lessonId } = action.data as QuizReviewEndActionData;

      return {
        ...state,
        lessonDetail: {
          ...state.lessonDetail,
          [lessonId]: {
            ...state.lessonDetail[lessonId],
            quizReview: null
          }
        }
      };
    }

    default:
      return state;
  }
};

const reducer = (state: CourseDetail.AppState = defaultState, action: ReduxAction<any>): CourseDetail.AppState => {
  switch (action.type) {
    case Actions.InitBegin: {
      return {
        ...state,
        status: Status.Loading
      };
    }
    case Actions.InitFailure: {
      return {
        ...state,
        status: Status.Failed
      };
    }
    case Actions.InitSuccess: {
      const { activeCourse, activeLesson } = action.data as InitSuccessActionData;
      return {
        ...state,
        activeCourseId: null,
        activeCourse,
        status: Status.Ready,
        lessonDetail: activeLesson
          ? {
              [activeLesson.id]: {
                ...activeLesson,
                error: null,
                status: "ready"
              }
            }
          : {}
      };
    }

    default:
      return reducerDetail(state, action);
  }
};

export const defaultState: CourseDetail.AppState = {
  activeCourse: null,
  activeCourseId: null,
  lessonDetail: {},
  error: null,
  id: "",
  searchText: null,
  status: Status.None
};
export const previewState: CourseDetail.AppState = {
  activeCourse: {
    activeLessonId: null,
    activeModuleId: null,
    allFaq: [],
    course: {
      completedLessonCount: 0,
      id: "dummy",
      isEnrolled: true,
      isMembership: false,
      name: "Dummy",
      portalId: "",
      registerPagePermalink: "/",
      thumbnailMediaId: null,
      totalLessonCount: 10,
      mediaSourceId: null
    },
    lessons: [],
    modules: []
  },
  activeCourseId: "dummy",
  lessonDetail: {},
  error: null,
  id: "",
  searchText: null,
  status: Status.Ready
};
export default wrapReducer({ defaultState, reducer, previewState });
